/**
 * HLS Video Player v2 - 支持 m3u8/HLS 格式视频播放
 * 
 * 修复版：
 * - 解决 defer 加载时序问题
 * - 兼容 Shopify media.sources 格式
 * - 更可靠的降级机制
 */

(function() {
    'use strict';

    const CONFIG = {
        hlsCdnUrl: 'https://cdn.jsdelivr.net/npm/hls.js@1.5.7/dist/hls.min.js',
        debug: true,
        maxRetries: 3
    };

    let hlsLoaded = false;
    let hjsInstance = null;
    const playerInstances = new Map();
    const initializedVideos = new WeakSet();

    // ==================== 工具函数 ====================

    function log(...args) {
        if (CONFIG.debug) {
            console.log('[HLS]', ...args);
        }
    }

    function error(...args) {
        if (CONFIG.debug) {
            console.error('[HLS ERROR]', ...args);
        }
    }

    /**
     * 动态加载 HLS.js
     */
    function loadHLS() {
        return new Promise((resolve, reject) => {
            if (window.Hls) {
                hlsLoaded = true;
                return resolve(window.Hls);
            }

            const script = document.createElement('script');
            script.src = CONFIG.hlsCdnUrl;
            script.onload = () => {
                hlsLoaded = true;
                log('HLS.js loaded');
                resolve(window.Hls);
            };
            script.onerror = (e) => {
                error('Failed to load HLS.js:', e);
                reject(e);
            };
            document.head.appendChild(script);
        });
    }

    /**
     * 检查是否原生支持 HLS (Safari/iOS)
     */
    function isNativeHLSSupported() {
        const video = document.createElement('video');
        return video.canPlayType('application/vnd.apple.mpegurl') !== '';
    }

    /**
     * 从 video 元素获取所有源，包括 Shopify 的 media.sources
     * 支持的检测方式：
     * 1. <source> 标签中的 src 和 type 属性
     * 2. 文件扩展名 (.m3u8, .mp4, .m3u8?...)
     * 3. MIME 类型
     */
    function analyzeSources(videoElement) {
        const sources = Array.from(videoElement.querySelectorAll('source'));
        const result = {
            all: [],
            hls: null,
            mp4: null,
            hasHLS: false,
            hasMP4: false
        };

        sources.forEach(source => {
            const src = (source.src || source.getAttribute('src') || '').trim();
            const type = (source.type || source.getAttribute('type') || '').trim();
            
            if (!src) return;

            const sourceInfo = { src, type, element: source };
            result.all.push(sourceInfo);

            // 检测 m3u8/HLS
            const isHLSURL = src.toLowerCase().includes('.m3u8') || 
                             src.toLowerCase().includes('m3u8');
            const isHLSType = type.includes('mpegurl') || 
                              type.includes('apple.mpegurl') ||
                              type === 'application/x-mpegURL';
            
            if (isHLSURL || isHLSType) {
                result.hls = src;
                result.hasHLS = true;
                log('Found HLS source:', src.substring(0, 80) + '...');
            }

            // 检测 MP4
            const isMP4URL = src.toLowerCase().includes('.mp4');
            const isMP4Type = type === 'video/mp4' || type.includes('mp4');
            
            if (isMP4URL || isMP4Type) {
                result.mp4 = src;
                result.hasMP4 = true;
                log('Found MP4 source:', src.substring(0, 80) + '...');
            }
        });

        // 如果没有从 source 找到，检查 data 属性
        if (!result.hls && videoElement.dataset.hlsSrc) {
            result.hls = videoElement.dataset.hlsSrc;
            result.hasHLS = true;
        }
        if (!result.mp4 && videoElement.dataset.mp4Src) {
            result.mp4 = videoElement.dataset.mp4Src;
            result.hasMP4 = true;
        }

        // 调试输出所有源
        if (CONFIG.debug && result.all.length > 0) {
            log('All sources found:', result.all.map(s => ({
                src: s.src.substring(0, 60),
                type: s.type
            })));
        } else if (CONFIG.debug) {
            log('No <source> elements found in video');
        }

        return result;
    }

    // ==================== 降级处理 ====================

    /**
     * 降级到 MP4 播放
     */
    function fallbackToMP4(videoElement, mp4Source, reason) {
        error('Falling back to MP4:', reason);
        
        if (!mp4Source) {
            error('No MP4 fallback available!');
            showErrorMessage(videoElement, 'Video format not supported');
            return false;
        }

        try {
            // 先销毁 HLS 实例
            destroyPlayer(videoElement);

            // 移除现有 sources
            const existingSources = videoElement.querySelectorAll('source');
            existingSources.forEach(s => s.remove());

            // 设置 MP4 源
            const source = document.createElement('source');
            source.src = mp4Source;
            source.type = 'video/mp4';
            videoElement.insertBefore(source, videoElement.firstChild);

            // 重载并播放
            videoElement.load();
            
            const playPromise = videoElement.play();
            if (playPromise !== undefined) {
                playPromise.catch(e => {
                    log('Autoplay blocked after fallback:', e.message);
                });
            }

            log('Successfully fell back to MP4');
            return true;

        } catch (err) {
            error('Fallback failed:', err);
            return false;
        }
    }

    /**
     * 显示错误消息（可选）
     */
    function showErrorMessage(videoElement, message) {
        // 可以在这里添加错误提示 UI
        log('Error:', message);
    }

    // ==================== HLS 播放器管理 ====================

    /**
     * 销毁播放器实例
     */
    function destroyPlayer(videoElement) {
        const instance = playerInstances.get(videoElement);
        if (instance) {
            try {
                instance.destroy();
                log('HLS instance destroyed');
            } catch (e) {
                error('Error destroying instance:', e);
            }
            playerInstances.delete(videoElement);
        }
    }

    /**
     * 使用 HLS.js 初始化
     */
    async function initWithHLSJS(videoElement, hlsURL, mp4URL) {
        try {
            const Hls = await loadHLS();

            if (!Hls.isSupported()) {
                log('HLS.js reports: NOT SUPPORTED');
                return fallbackToMP4(videoElement, mp4URL, 'HLS.js not supported in this browser');
            }

            // 清理旧实例
            destroyPlayer(videoElement);

            // 创建新实例
            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: false,
                backBufferLength: 90,
                maxBufferLength: 30,
                maxMaxBufferLength: 60,
                startLevel: -1,
                capLevelToPlayerSize: true,
                debug: CONFIG.debug
            });

            playerInstances.set(videoElement, hls);

            // 事件监听
            hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
                log(`Manifest parsed! ${data.levels.length} quality level(s) available`);
                
                // 尝试自动播放
                const promise = videoElement.play();
                if (promise !== undefined) {
                    promise.catch(e => log('Autoplay prevented:', e.message));
                }
            });

            hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
                if (CONFIG.debug && data.level >= 0) {
                    const level = hls.levels[data.level];
                    if (level) {
                        log(`Quality switched to: ${level.height}p (${level.bitrate / 1000}kbps)`);
                    }
                }
            });

            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            error('Network fatal error:', data.details);
                            // 尝试恢复
                            log('Attempting network recovery...');
                            hls.startLoad();
                            break;

                        case Hls.ErrorTypes.MEDIA_ERROR:
                            error('Media fatal error:', data.details);
                            log('Attempting media recovery...');
                            hls.recoverMediaError();
                            break;

                        default:
                            error('Unrecoverable error:', data.type, data.details);
                            destroyPlayer(videoElement);
                            fallbackToMP4(videoElement, mp4URL, `${data.type}: ${data.details}`);
                            break;
                    }
                } else {
                    error('Non-fatal error:', data.type, data.details);
                }
            });

            // 加载并附加到 video
            hls.loadSource(hlsURL);
            hls.attachMedia(videoElement);

            log('HLS player initialized successfully');
            return true;

        } catch (err) {
            error('HLS.js initialization error:', err);
            return fallbackToMP4(videoElement, mp4URL, err.message);
        }
    }

    /**
     * 使用原生支持初始化 (Safari)
     */
    function initNative(videoElement, hlsURL, mp4URL) {
        log('Using native HLS support');

        try {
            videoElement.src = hlsURL;

            const handleError = (e) => {
                const errorCode = videoElement.error?.code;
                error(`Native HLS error (code ${errorCode}):`, videoElement.error?.message);
                videoElement.removeEventListener('error', handleError);
                fallbackToMP4(videoElement, mp4URL, `Native error code: ${errorCode}`);
            };

            videoElement.addEventListener('error', handleError, { once: true });
            videoElement.load();

            const promise = videoElement.play();
            if (promise !== undefined) {
                promise.catch(e => log('Autoplay prevented:', e.message));
            }

            return true;

        } catch (err) {
            error('Native init error:', err);
            return fallbackToMP4(videoElement, mp4URL, err.message);
        }
    }

    // ==================== 主初始化函数 ====================

    /**
     * 初始化单个 video 元素
     */
    async function initVideo(videoElement) {
        // 防止重复初始化
        if (initializedVideos.has(videoElement)) {
            log('Video already initialized, skipping');
            return;
        }

        // 验证元素
        if (!videoElement || videoElement.tagName !== 'VIDEO') {
            error('Invalid element passed to initVideo');
            return;
        }

        // 标记为已初始化
        initializedVideos.add(videoElement);
        log('Initializing video element...');

        // 分析源
        const sources = analyzeSources(videoElement);

        if (!sources.hasHLS) {
            log('No HLS source found, using native HTML5 video playback');
            return; // 没有 m3u8，让浏览器原生处理
        }

        // 根据浏览器能力选择策略
        if (isNativeHLSSupported()) {
            initNative(videoElement, sources.hls, sources.mp4);
        } else {
            await initWithHLSJS(videoElement, sources.hls, sources.mp4);
        }
    }

    /**
     * 初始化页面上所有带 data-hls 的 video
     */
    function initAllVideos() {
        const videos = document.querySelectorAll('video[data-hls="true"]');
        log(`Found ${videos.length} video(s) with data-hls="true"`);
        
        videos.forEach((video, index) => {
            // 稍微错开初始化时间，避免同时请求太多资源
            setTimeout(() => {
                initVideo(video);
            }, index * 100);
        });
    }

    // ==================== 启动 ====================

    // 多种启动策略确保初始化
    
    // 策略1: DOMContentLoaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            setTimeout(initAllVideos, 200); // 给 Shopify 渲染留点时间
        });
    } else {
        setTimeout(initAllVideos, 200);
    }

    // 策略2: window.onload (作为备份)
    window.addEventListener('load', () => {
        setTimeout(initAllVideos, 500);
    });

    // 策略3: 监听 DOM 变化 (动态加载的 sections)
    const observer = new MutationObserver(debounce((mutations) => {
        let hasNewVideo = false;
        
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE) continue;
                
                if (node.matches && node.matches('video[data-hls="true"]')) {
                    hasNewVideo = true;
                    break;
                }
                
                if (node.querySelectorAll) {
                    const newVideos = node.querySelectorAll('video[data-hls="true"]');
                    if (newVideos.length > 0) {
                        hasNewVideo = true;
                        break;
                    }
                }
            }
        }
        
        if (hasNewVideo) {
            log('Detected new video element(s), initializing...');
            setTimeout(initAllVideos, 50);
        }
    }, 150));

    observer.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true
    });

    // 防抖函数
    function debounce(fn, delay) {
        let timer = null;
        return function(...args) {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    // 暴露全局 API
    window.HLSPlayer = {
        init: initVideo,
        initAll: initAllVideos,
        refresh: initAllVideos,
        destroy: destroyPlayer,
        getStats: () => ({
            loaded: hlsLoaded,
            instancesCount: playerInstances.size,
            initializedCount: initializedVideos.size ? '?' : 0 // WeakSet 无法计数
        })
    };

    log('HLS Player v2 initialized');

})();
